import type { ReactElement } from 'react';
import type { Position } from '../api/schemas';
import { SIDE_LABEL } from '../domain/labels';
import { formatDateTime } from '../lib/datetime';
import { Banner } from './Banner';
import { KeyValue } from './KeyValue';
import { Percent, Pnl, Price, Quantity, Usdt } from './Money';
import { NoData } from './NoData';
import { StatusPill } from './StatusPill';

export interface PositionCardProps {
  readonly position: Position | null;
}

/**
 * The open position, with its protective levels.
 *
 * Stop Loss, Take Profit and the liquidation price are shown together on
 * purpose: the distance between the stop and the liquidation price is the one
 * number that decides whether a 30x position is survivable, and an operator
 * should never have to open two screens to compare them.
 */
export function PositionCard({ position }: PositionCardProps): ReactElement {
  if (position === null) {
    return (
      <div className="stack stack--tight">
        <StatusPill tone="neutral" srPrefix="Позиция">
          Открытой позиции нет
        </StatusPill>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Защитные ордера не требуются. Стратегия ожидает следующего планового решения.
        </p>
      </div>
    );
  }

  return (
    <div className="stack stack--tight">
      <div className="row">
        <StatusPill tone={position.side === 'LONG' ? 'ok' : 'info'} srPrefix="Сторона позиции">
          {SIDE_LABEL[position.side]}
        </StatusPill>
        <StatusPill tone="neutral" srPrefix="Инструмент">
          {position.symbol}
        </StatusPill>
        <StatusPill tone="warn" srPrefix="Плечо">
          Плечо {position.leverage}x
        </StatusPill>
      </div>

      {position.ageWarning && (
        <Banner tone="warn" title="Позиция держится дольше ожидаемого.">
          Проверьте, что защитные ордера на месте и стратегия не застряла в ожидании закрытия.
        </Banner>
      )}

      {position.markPrice === null && (
        <Banner tone="warn" title="Метка цены недоступна.">
          Текущая цена с площадки не получена, поэтому нереализованный результат по позиции не
          рассчитан. Пустые значения означают «неизвестно», а не «ноль».
        </Banner>
      )}

      <KeyValue
        rows={[
          { label: 'Объём', value: <Quantity value={position.quantity} /> },
          { label: 'Маржа', value: <Usdt value={position.marginAmount} /> },
          { label: 'Цена входа', value: <Price value={position.entryPrice} /> },
          {
            label: 'Цена mark',
            value:
              position.markPrice === null ? (
                <NoData reasonRu="метка цены с площадки не получена" />
              ) : (
                <Price value={position.markPrice} />
              ),
          },
          {
            label: 'Stop Loss',
            value: (
              <span>
                <Price value={position.stopLossPrice} /> <span className="muted">·</span>{' '}
                <Percent value={position.slRoiPct} /> ROI
              </span>
            ),
          },
          {
            label: 'Take Profit',
            value: (
              <span>
                <Price value={position.takeProfitPrice} /> <span className="muted">·</span>{' '}
                <Percent value={position.tpRoiPct} /> ROI
              </span>
            ),
          },
          {
            label: 'Цена ликвидации',
            value:
              position.liquidationPrice === null ? (
                <span className="muted">—</span>
              ) : (
                <span className="text-danger">
                  <Price value={position.liquidationPrice} />
                </span>
              ),
          },
          {
            label: 'PnL до комиссий',
            value:
              position.grossPnl === null ? (
                <NoData reasonRu="нет метки цены для расчёта" />
              ) : (
                <Pnl value={position.grossPnl} />
              ),
          },
          {
            label: 'PnL после комиссий',
            value:
              position.netPnl === null ? (
                <NoData reasonRu="нет метки цены для расчёта" />
              ) : (
                <Pnl value={position.netPnl} />
              ),
          },
          { label: 'Открыта', value: formatDateTime(position.openedAt) },
        ]}
      />
    </div>
  );
}
